import { LoaderService } from '@/common/loader/loader.service';
import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	DestroyRef,
	OnInit,
	inject,
	input,
	output,
	signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { finalize, map, pipe } from 'rxjs';
import { MaxRoomStatus, OfferTermMaxRooms } from '../../bag-transport-term.model';
import { BagTransportTermChangeRoomAvailabilityApiService } from '../bag-transport-term-change-room-availability-api.service';
import { rxMethod } from '@ngrx/signals/rxjs-interop';

@Component({
	selector: 'app-bag-transport-term-change-max-rooms',
	standalone: true,
	imports: [CommonModule, FormsModule, ReactiveFormsModule, MatButtonModule, MatInputModule],
	templateUrl: './bag-transport-term-change-max-rooms.component.html',
	styleUrl: './bag-transport-term-change-max-rooms.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BagTransportTermChangeMaxRoomsComponent implements OnInit {
	readonly termId = input.required<number>();
	readonly maxRoomsValue = input.required<number>();
	readonly refreshRooms = output<void>();
	readonly refreshMaxRooms = output<void>();

	maxRooms = signal<OfferTermMaxRooms>(null);
	maxRoomsForm: FormGroup;

	private readonly changeMaxRoomsAfterRefresh = rxMethod<number>(
		pipe(
			map((value) => {
				this.maxRooms.set({ maxRooms: value, mode: MaxRoomStatus.EDIT });
			}),
		),
	);

	readonly MaxRoomStatus = MaxRoomStatus;

	private readonly formBuilder = inject(FormBuilder);
	private readonly loader = inject(LoaderService);
	private readonly cdr = inject(ChangeDetectorRef);
	private readonly destroyRef = inject(DestroyRef);
	private readonly bagTransportTermChangeRoomAvailabilityApiService = inject(
		BagTransportTermChangeRoomAvailabilityApiService,
	);

	ngOnInit(): void {
		this.prepareMaxRoomForm();
		this.changeMaxRoomsAfterRefresh(this.maxRoomsValue);
	}

	editMaxRooms(): void {
		this.maxRooms.set({ maxRooms: this.maxRoomsValue(), mode: MaxRoomStatus.SAVE });
		this.maxRoomsForm.controls.maxRooms.setValue(this.maxRooms().maxRooms);
	}

	changeMaxRooms(): void {
		this.maxRoomsForm.markAllAsTouched();
		if (!this.maxRoomsForm.valid) {
			return;
		}
		const dataToSend: OfferTermMaxRooms = {
			maxRooms: this.maxRoomsForm.controls.maxRooms.value,
		};

		this.loader.show('changeProductMaxRooms');
		this.bagTransportTermChangeRoomAvailabilityApiService
			.changeProductMaxRooms(this.termId(), dataToSend)
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				finalize(() => this.loader.hide('changeProductMaxRooms')),
			)
			.subscribe(() => {
				this.clearMaxRoomsForm();
				this.refreshMaxRooms.emit();
				this.refreshRooms.emit();
			});
	}

	cancelMaxRooms(): void {
		this.maxRooms.set({ maxRooms: this.maxRoomsValue() ?? 0, mode: MaxRoomStatus.EDIT });
		this.clearMaxRoomsForm();
		this.cdr.detectChanges();
	}

	private prepareMaxRoomForm(): void {
		this.maxRoomsForm = this.formBuilder.group({
			maxRooms: [0, Validators.required],
		});
	}

	private clearMaxRoomsForm(): void {
		this.maxRoomsForm.reset();
	}
}
